// location-step.tsx — "Your details" screen of the booking flow.
import { ReactNode, useState } from "react";
import { Input } from "../../components/ui/input";
import { validateEmail } from "../../lib/validateEmail";
import { cn } from "../../lib/util";
import { FormField, fieldControlClass, fieldErrorClass } from "./field";

interface LocationStepProps {
  location: string;
  onLocationChange: (value: "studio" | "home") => void;
  homeAddress?: string;
  onHomeAddressChange?: (value: string) => void;
  landmark?: string;
  onLandmarkChange?: (value: string) => void;
  numberOfFaces: string;
  customFacesCount: string;
  onNumberOfFacesChange: (value: string) => void;
  onCustomFacesChange: (value: string) => void;
  error?: boolean;
  homeAddressError?: any;
  facesError?: boolean;
  formData?: any;
  onFieldChange?: (field: string, value: any) => void;
  errors?: Record<string, boolean>;
}

/** Pill option (radio) — used for binary choices and the faces counter. */
const pillClass =
  "flex min-h-[44px] min-w-[3rem] cursor-pointer items-center justify-center rounded-full " +
  "border border-[#E3C9C8] bg-white px-5 text-base text-[#3a2e2c] transition " +
  "peer-checked:border-[#B87775] peer-checked:bg-[#B87775] peer-checked:text-white " +
  "peer-focus-visible:ring-2 peer-focus-visible:ring-[#B87775] peer-focus-visible:ring-offset-2 " +
  "hover:border-[#B87775]";

function Pill({
  name,
  value,
  checked,
  onChange,
  children,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  children: ReactNode;
}) {
  const id = `${name}-${value}`;
  return (
    <label htmlFor={id} className="relative">
      <input
        type="radio"
        id={id}
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="peer sr-only"
      />
      <span className={pillClass}>{children}</span>
    </label>
  );
}

export function LocationStep({
  location,
  onLocationChange,
  homeAddress,
  onHomeAddressChange,
  landmark,
  onLandmarkChange,
  numberOfFaces,
  customFacesCount,
  onNumberOfFacesChange,
  onCustomFacesChange,
  error,
  homeAddressError,
  facesError,
  formData,
  onFieldChange,
  errors,
}: LocationStepProps) {
  // Local "touched" state powers validate-on-blur: a field only scolds the
  // user once they've left it, and the message clears the moment it's valid.
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const touch = (field: string) => setTouched((t) => ({ ...t, [field]: true }));

  const name = formData?.name ?? "";
  const email = formData?.email ?? "";
  const phone = formData?.phoneNumber ?? "";

  const nameMsg = !name.trim() ? "Please enter your full name." : undefined;
  const emailMsg = !email.trim()
    ? "Please enter your email address."
    : !validateEmail(email)
    ? "That doesn't look like a valid email address."
    : email.length > 100
    ? "That email address is too long."
    : undefined;
  const phoneMsg = !phone.trim()
    ? "Please enter your phone number."
    : phone.length < 10
    ? "Please enter at least 10 digits."
    : phone.length > 15
    ? "That phone number looks too long."
    : undefined;

  // Show a message when the field was touched OR a submit attempt flagged it.
  const show = (field: string, msg?: string) =>
    (touched[field] || errors?.[field]) && msg ? msg : undefined;

  return (
    <div className="space-y-10">
      {/* ── About you ───────────────────────────────────────── */}
      <fieldset className="space-y-6">
        <legend className="mb-1 text-xs uppercase tracking-[0.2em] text-[#B87775]">
          About you
        </legend>

        <FormField id="name" label="Full name" required error={show("name", nameMsg)}>
          {(a) => (
            <Input
              {...a}
              type="text"
              autoComplete="name"
              placeholder="e.g. Amara Okafor"
              value={name}
              onChange={(e) => onFieldChange?.("name", e.target.value)}
              onBlur={() => touch("name")}
              className={cn(fieldControlClass, show("name", nameMsg) && fieldErrorClass)}
            />
          )}
        </FormField>

        <div className="grid gap-6 md:grid-cols-2">
          <FormField
            id="email"
            label="Email address"
            required
            hint="Your confirmation and any updates go here."
            error={show("email", emailMsg)}
          >
            {(a) => (
              <Input
                {...a}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                maxLength={100}
                onChange={(e) => onFieldChange?.("email", e.target.value.slice(0, 100))}
                onBlur={() => touch("email")}
                className={cn(
                  fieldControlClass,
                  show("email", emailMsg) && fieldErrorClass
                )}
              />
            )}
          </FormField>

          <FormField
            id="phone"
            label="Phone number"
            required
            hint="In case we need to reach you on the day."
            error={show("phone", phoneMsg)}
          >
            {(a) => (
              <Input
                {...a}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+1 (902) 555-0123"
                value={phone}
                maxLength={15}
                onChange={(e) => onFieldChange?.("phoneNumber", e.target.value)}
                onBlur={() => touch("phone")}
                className={cn(
                  fieldControlClass,
                  show("phone", phoneMsg) && fieldErrorClass
                )}
              />
            )}
          </FormField>
        </div>

        {/* First experience — a warm yes/no, not a dropdown */}
        <fieldset>
          <legend className="block font-serif text-[17px] leading-snug text-[#3a2e2c]">
            Is this your first glam with Charmé?
            <span className="text-[#B87775]" aria-hidden="true">
              {" "}
              *
            </span>
          </legend>
          <div className="mt-3 flex gap-3">
            <Pill
              name="firstExperience"
              value="true"
              checked={formData?.firstExperience === true}
              onChange={() => onFieldChange?.("firstExperience", true)}
            >
              Yes, my first
            </Pill>
            <Pill
              name="firstExperience"
              value="false"
              checked={formData?.firstExperience === false}
              onChange={() => onFieldChange?.("firstExperience", false)}
            >
              I've been before
            </Pill>
          </div>
          <div aria-live="polite">
            {errors?.firstExperience && (
              <p className="mt-2 text-sm text-[#c0504d]">
                Please let us know if this is your first visit.
              </p>
            )}
          </div>
        </fieldset>
      </fieldset>

      <div className="h-px bg-[#EAD7D6]" />

      {/* ── Where & how many ────────────────────────────────── */}
      <fieldset className="space-y-6">
        <legend className="mb-1 text-xs uppercase tracking-[0.2em] text-[#B87775]">
          Where & how many
        </legend>

        {/* Glam spot — two considered cards */}
        <fieldset>
          <legend className="block font-serif text-[17px] leading-snug text-[#3a2e2c]">
            Where would you like to be glammed?
            <span className="text-[#B87775]" aria-hidden="true">
              {" "}
              *
            </span>
          </legend>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {[
              {
                value: "studio" as const,
                title: "Studio session",
                blurb: "Come to our Halifax studio.",
              },
              {
                value: "home" as const,
                title: "Home service",
                blurb: "We come to you, anywhere in the city.",
              },
            ].map((opt) => {
              const id = `glamspot-${opt.value}`;
              return (
                <label key={opt.value} htmlFor={id} className="relative block">
                  <input
                    type="radio"
                    id={id}
                    name="glamSpot"
                    value={opt.value}
                    checked={location === opt.value}
                    onChange={() => onLocationChange(opt.value)}
                    className="peer sr-only"
                  />
                  <span
                    className={cn(
                      "flex h-full cursor-pointer flex-col gap-1 rounded-xl border border-[#E3C9C8] bg-white px-4 py-4 transition",
                      "hover:border-[#B87775]/70",
                      "peer-checked:border-[#B87775] peer-checked:bg-[#FBEFEF] peer-checked:ring-1 peer-checked:ring-[#B87775]",
                      "peer-focus-visible:ring-2 peer-focus-visible:ring-[#B87775] peer-focus-visible:ring-offset-2"
                    )}
                  >
                    <span className="font-serif text-lg text-[#3a2e2c]">
                      {opt.title}
                    </span>
                    <span className="text-sm text-[#7c6c6a]">{opt.blurb}</span>
                  </span>
                </label>
              );
            })}
          </div>
          <div aria-live="polite">
            {error && (
              <p className="mt-2 text-sm text-[#c0504d]">
                Please choose where you'd like your session.
              </p>
            )}
          </div>
        </fieldset>

        {location === "home" && (
          <div className="grid gap-6 rounded-xl border border-[#EAD7D6] bg-[#FBEFEF]/50 p-4 md:grid-cols-2">
            <FormField
              id="homeAddress"
              label="Home address"
              required
              error={
                homeAddressError && !homeAddress?.trim()
                  ? "Please enter your home address."
                  : undefined
              }
            >
              {(a) => (
                <Input
                  {...a}
                  autoComplete="street-address"
                  value={homeAddress ?? ""}
                  onChange={(e) => onHomeAddressChange?.(e.target.value)}
                  placeholder="Street, unit, city"
                  className={cn(
                    fieldControlClass,
                    homeAddressError && !homeAddress?.trim() && fieldErrorClass
                  )}
                />
              )}
            </FormField>
            <FormField
              id="landmark"
              label="Landmark"
              optional
              hint="Anything that helps us find you."
            >
              {(a) => (
                <Input
                  {...a}
                  value={landmark ?? ""}
                  onChange={(e) => onLandmarkChange?.(e.target.value)}
                  placeholder="e.g. across from the park"
                  className={fieldControlClass}
                />
              )}
            </FormField>
          </div>
        )}

        {/* Number of faces */}
        <fieldset>
          <legend className="block font-serif text-[17px] leading-snug text-[#3a2e2c]">
            How many faces are we glamming?
            <span className="text-[#B87775]" aria-hidden="true">
              {" "}
              *
            </span>
          </legend>
          <div className="mt-3 flex flex-wrap gap-2">
            {["1", "2", "3", "4", "5", "more"].map((num) => (
              <Pill
                key={num}
                name="numberOfFaces"
                value={num}
                checked={numberOfFaces === num}
                onChange={() => onNumberOfFacesChange(num)}
              >
                {num === "more" ? "More than 5" : num}
              </Pill>
            ))}
          </div>

          {numberOfFaces === "more" && (
            <FormField
              id="customFaces"
              label="Exactly how many?"
              required
              className="mt-4 max-w-xs"
              error={
                facesError && (!customFacesCount || parseInt(customFacesCount, 10) < 6)
                  ? "Please enter 6 or more."
                  : undefined
              }
            >
              {(a) => (
                <Input
                  {...a}
                  type="number"
                  inputMode="numeric"
                  min="6"
                  value={customFacesCount}
                  onChange={(e) => onCustomFacesChange(e.target.value)}
                  placeholder="6 or more"
                  className={cn(
                    fieldControlClass,
                    facesError &&
                      (!customFacesCount || parseInt(customFacesCount, 10) < 6) &&
                      fieldErrorClass
                  )}
                />
              )}
            </FormField>
          )}

          <div aria-live="polite">
            {facesError && numberOfFaces !== "more" && (
              <p className="mt-2 text-sm text-[#c0504d]">
                Please tell us how many faces to glam.
              </p>
            )}
          </div>
        </fieldset>
      </fieldset>
    </div>
  );
}
